'use client'

import { Spinner } from '@/components/extension/spinner'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Minus, Plus } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useProductDetailSafe } from '../../context/product-detail-context'
import { useProductDetailStore } from '../../hooks/use-product-detail-store'

interface ProductInfoAddCartProps {
  isGiveaway?: boolean
  isStopSale?: boolean
  isDisabled?: boolean
}

export const ProductInfoAddCart = ({
  isGiveaway = false,
  isStopSale,
  isDisabled,
}: ProductInfoAddCartProps) => {
  const t = useTranslations('product_detail.title')
  const {
    quantity,
    handleQuantityChange,
    handleIncrement,
    handleDecrement,
    handleKeyDown,
    isPendingAddToCart,
    handleAddToCartClick,
    isDisableSize,
  } = useProductDetailStore()

  const { sortedColorOptions, sizeOptions, sizeSelected, product } =
    useProductDetailSafe()

  // Đổi màu vẫn giữ size đang chọn, nên size đó có thể đã hết hàng ở màu mới
  const isSelectedSizeOutOfStock =
    !product?.is_setdo && !!sizeSelected && isDisableSize(sizeSelected)

  if (isGiveaway) {
    return null
  }

  if (product?.coming_soon)
    return <DisabledAddCartButton message={t('coming_soon')} />

  if (!sortedColorOptions.length && !sizeOptions.length)
    return <DisabledAddCartButton message={t('out_of_stock')} />

  if (isStopSale)
    return <DisabledAddCartButton message={t('stopped_selling')} />

  if (isSelectedSizeOutOfStock)
    return <DisabledAddCartButton message={t('out_of_stock')} />

  return (
    <div
      className={cn(
        'mt-4 flex h-13 w-full items-center gap-2 lg:!mt-6',
        isDisabled && 'pointer-events-none opacity-50'
      )}
    >
      <div className='flex h-full w-28 shrink-0 items-center justify-between rounded-full border border-neutral-300 bg-light px-2 text-base text-neutral-900'>
        <button
          className='flex h-8 w-8 items-center justify-center'
          onClick={handleDecrement}
          aria-label={t('decrease_quantity')}
          ga-tracking-value='product-detail__quantity__decrement'
          ga-tracking-label='product-detail__quantity__decrement'
        >
          <Minus size={14} className='text-neutral-900' />
        </button>
        <label htmlFor='quantity-input' className='sr-only'>
          {t('quantity')}
        </label>
        <input
          id='quantity-input'
          type='text'
          className='w-8 border-none bg-inherit text-center font-sans text-base font-medium outline-none'
          value={quantity}
          onChange={handleQuantityChange}
          onKeyDown={handleKeyDown}
          aria-label={t('quantity')}
        />
        <button
          className='flex h-8 w-8 items-center justify-center'
          onClick={handleIncrement}
          aria-label={t('increase_quantity')}
          ga-tracking-value='product-detail__quantity__increment'
          ga-tracking-label='product-detail__quantity__increment'
        >
          <Plus size={14} className='text-neutral-900' />
        </button>
      </div>
      <Button
        className='h-full flex-1 gap-2 rounded-full bg-neutral-900 hover:bg-neutral-800'
        variant={'default'}
        onClick={() => handleAddToCartClick()}
        id='product-detail-add-cart'
        disabled={isPendingAddToCart}
        ga-tracking-value={
          product?.is_setdo
            ? 'product-detail__add_to_cart_set-do'
            : 'product-detail__add_to_cart'
        }
        ga-tracking-label='product-detail__add_to_cart'
      >
        <Image
          className='h-4 w-4'
          src='/icons/product/icon-cart.svg'
          width={16}
          height={16}
          alt='add cart icon'
        />
        <p className='font-criteria text-base font-medium normal-case leading-6'>
          <span className='flex items-center gap-2'>
            {isPendingAddToCart
              ? t('adding')
              : product?.preorder
                ? t('preorder')
                : t('add_to_cart')}
            {isPendingAddToCart && <Spinner className='h-4 w-4 text-white' />}
          </span>
        </p>
      </Button>
    </div>
  )
}

interface DisabledAddCartButtonProps {
  message: string
}

const DisabledAddCartButton = ({ message }: DisabledAddCartButtonProps) => {
  return (
    <Button
      className='flex w-full items-center gap-2'
      variant={'default'}
      disabled
      id='product-detail-add-cart'
    >
      <Image
        className='h-4 w-4 object-contain'
        src='/icons/product/icon-cart.svg'
        width={16}
        height={16}
        alt='add cart icon'
      />
      <p className='font-criteria text-base font-medium normal-case leading-6'>
        <span className='flex items-center gap-2'>{message}</span>
      </p>
    </Button>
  )
}
